import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CloseIcon } from './icons'
import styles from './Toast.module.css'

export type ToastTone = 'info' | 'success' | 'error' | 'update'

export interface ToastOptions {
  message: ReactNode
  tone?: ToastTone
  /** e.g. { label: 'Reload', onClick } for "Update available". */
  action?: { label: string; onClick: () => void }
  /** ms before auto-dismiss; 0 = stays until closed. Errors and updates default to 0. */
  duration?: number
}

export interface ToastItem extends Required<Pick<ToastOptions, 'tone' | 'duration'>>, Omit<ToastOptions, 'tone' | 'duration'> {
  id: number
}

interface ToastApi { show: (t: ToastOptions) => number; dismiss: (id: number) => void }

const ToastContext = createContext<ToastApi | null>(null)

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const next = useRef(1)
  const dismiss = useCallback((id: number) => setItems(list => list.filter(t => t.id !== id)), [])
  const show = useCallback((t: ToastOptions) => {
    const id = next.current++
    const tone = t.tone ?? 'info'
    const duration = t.duration ?? (tone === 'error' || tone === 'update' ? 0 : 5000)
    setItems(list => [...list.slice(-3), { ...t, id, tone, duration }])
    return id
  }, [])
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss])
  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastRegion items={items} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

/** Live regions stay mounted so screen readers announce toasts reliably. */
export function ToastRegion({ items, onDismiss }: { items: ToastItem[]; onDismiss: (id: number) => void }) {
  return (
    <div className={styles.region}>
      <div role="status" aria-live="polite" className={styles.stack}>
        {items.filter(t => t.tone !== 'error').map(t => <Toast key={t.id} item={t} onDismiss={onDismiss} />)}
      </div>
      <div role="alert" aria-live="assertive" className={styles.stack}>
        {items.filter(t => t.tone === 'error').map(t => <Toast key={t.id} item={t} onDismiss={onDismiss} />)}
      </div>
    </div>
  )
}

export function Toast({ item, onDismiss, inline = false }: { item: ToastItem; onDismiss: (id: number) => void; inline?: boolean }) {
  useEffect(() => {
    if (!item.duration) return
    const t = setTimeout(() => onDismiss(item.id), item.duration)
    return () => clearTimeout(t)
  }, [item.id, item.duration, onDismiss])
  return (
    <div className={`${styles.toast} ${styles[item.tone]} ${inline ? styles.inline : ''}`}>
      <span className={styles.dot} aria-hidden="true" />
      <div className={styles.message}>{item.message}</div>
      {item.action && (
        <button type="button" className={styles.action} onClick={() => { item.action?.onClick(); onDismiss(item.id) }}>
          {item.action.label}
        </button>
      )}
      <button type="button" className={styles.close} aria-label="Dismiss notification" onClick={() => onDismiss(item.id)}>
        <CloseIcon size={14} />
      </button>
    </div>
  )
}
